import classNames from 'classnames';
import { useCallback, useContext } from 'react';

import i18n from '@nextroll/ar-i18n';
import {
    Option,
    Select, // @ts-ignore - no types for ar-style-base
} from '@nextroll/ar-style-base';

import {
    setFieldFactory,
    setShowFieldErrorFactory,
} from '../../../actions/sign-up/SignUpActions';
import { BU_ROLLWORKS } from '../../../constants';
import ThemeWrapperContext from '../../../contexts/ThemeWrapperContext';
import SignUpContext from '../../../contexts/sign-up/SignUpContext';
import { ERROR_MESSAGES } from '../../../errors';
import usePrefillFromSearchParams from '../../../hooks/sign-up/usePrefillFromSearchParams';
import { isNorthAmericanCountry } from '../../../utils/common';

// These are not a constant because of i18n.
export const getOptions = (): { code: string; displayName: string }[] => {
    let countryList = Object.entries(i18n.COUNTRY_CODES).map(([key]) => ({
        code: key,
        displayName: i18n.getCountryName(key),
    }));

    const usca = countryList.filter((x) => isNorthAmericanCountry(x.code));
    countryList = countryList.filter((x) => !isNorthAmericanCountry(x.code));

    countryList.sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    countryList.unshift(...usca);

    return countryList;
};

// TODO: restore `required` decorator
export const validate = (value: string, businessUnit: string) => {
    if (value === '') {
        return i18n.gettext(ERROR_MESSAGES.required_field);
    }
    if (businessUnit === BU_ROLLWORKS && !isNorthAmericanCountry(value)) {
        // need to show different branding before vs. after ProjectX launch
        return i18n.gettext(ERROR_MESSAGES.adroll_abm_country_not_allowed);
    }
    return null;
};

export const searchCallback = (search: string, optionValue: string) =>
    i18n
        .getCountryName(optionValue)
        .toLowerCase()
        .includes(search.toLowerCase());

const CountryCode = () => {
    const { state, dispatch } = useContext(SignUpContext);
    const { businessUnit } = useContext(ThemeWrapperContext);

    const isValid = useCallback(
        (value: string) => validate(value, businessUnit),
        [businessUnit]
    );

    usePrefillFromSearchParams('country_code', 'countryCode', isValid);

    const field = state.get('fields').countryCode;
    const setField = setFieldFactory(dispatch, 'countryCode');
    const onChange = (value: string) => {
        const err = isValid(value);
        setField(value, err === null, err);
        if (value !== field.value) {
            setFieldFactory(dispatch, 'region')(
                '',
                !isNorthAmericanCountry(value),
                null
            );
        }
    };
    const setShowFieldError = setShowFieldErrorFactory(dispatch, 'countryCode');
    // This is calculated on-the-fly because we cannot trigger an onChange from the onClose (which is not
    // feasible because we don't have access to the new value yet)
    const error = isValid(field.value);

    return (
        <div
            className={classNames('form-group eh-select', {
                'has-error': field.showError && error,
                'no-selected': field.value === '',
            })}
        >
            <div className='eh-input-field-wrapper has-feedback has-feedback-right'>
                {/* show over-select label when there is a selected country code */}
                {field.value && (
                    <span className='eh-form-input-label-over'>
                        {i18n.gettext('Company Country')}
                    </span>
                )}
                <Select
                    i18n={i18n}
                    className='eh-input-country-code'
                    placeholder={i18n.gettext('Company Country')}
                    showSearch={true}
                    searchCallback={searchCallback}
                    onOptionSelect={onChange}
                    onOpen={() => setShowFieldError(false)}
                    onClose={() => setShowFieldError(true)}
                >
                    {getOptions().map((opt) => (
                        <Option
                            key={opt.code}
                            value={opt.code}
                            selected={opt.code === field.value}
                        >
                            {i18n.gettext(opt.displayName)}
                        </Option>
                    ))}
                </Select>
                <i className='form-control-feedback fa fa-globe-americas' />
            </div>
            {field.showError && error && (
                <span className='help-block ar-input-group-help'>{error}</span>
            )}
        </div>
    );
};

export default CountryCode;
